/* MS Belts 3.0, sitewide behaviour. Loaded with defer by layout/theme.liquid, after ms-track.js.
   Covers: modal dialogs (menu drawer, cart drawer, collection filter drawer), header dropdowns and
   full-width menu panels (one open at a time, Escape, hover intent with a mouse), the sticky header's
   scrolled state, quantity steppers [data-qty], AJAX add to cart for <form action="/cart/add"
   data-ajax-cart>, and cart drawer updates through the Section Rendering API (section "cart-drawer").
   Belt builds: cart lines that share the hidden property _ms_group stay together. A new belt quantity
   is sent for every line of the group in one /cart/update.js call (updates keyed by line item key),
   removing the belt removes its add-ons, and an add-on can still be removed on its own.
   No dependencies, no cart request on page load.
   Without this file the site still works: the menu opens through #nav-drawer, the cart link goes to
   the cart page, product forms post natively.
   Public API: window.MS.openCart(opener, source), window.MS.refreshCart(), window.MS.openDialog(dialog, opener),
   window.MS.closeDialog(dialog), window.MS.announce(message, scope). */
(function () {
  'use strict';

  if (window.MS) return;

  var doc = document;
  var config = readConfig();
  var routes = config.routes || {};
  var SECTION = config.cartSection || 'cart-drawer';
  var TEXT = {
    adding: 'Adding\u2026',
    added: 'Added to your cart.',
    updated: 'Cart updated.',
    empty: 'Your cart is empty.',
    addFailed: 'This item could not be added to your cart. Please try again.',
    changeFailed: 'That change could not be saved. Please try again.',
    offline: 'The cart could not be updated. Check your connection and try again.',
    noteFailed: 'Your note could not be saved. You can add it again at checkout.'
  };

  function readConfig() {
    var element = doc.getElementById('ms-config');
    try {
      return element ? JSON.parse(element.textContent) || {} : {};
    } catch (error) {
      return {};
    }
  }

  function all(root, selector) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(selector));
  }

  function track(name, params) {
    try {
      if (window.MSTrack && typeof window.MSTrack.event === 'function') window.MSTrack.event(name, params || {});
    } catch (error) {
      /* Tracking must never break the page. */
    }
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function isRendered(element) {
    return !!element && doc.contains(element) && element.getClientRects().length > 0;
  }

  /* ---------- Live announcements ---------- */
  /* The page-level live region exists from the start: screen readers ignore a region that is
     created and filled in the same moment. */
  var pageLive = doc.createElement('p');
  pageLive.className = 'visually-hidden';
  pageLive.setAttribute('role', 'status');
  doc.body.appendChild(pageLive);

  function setLive(region, message) {
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function announce(message, scope) {
    var dialog = scope && typeof scope.closest === 'function' ? scope.closest('dialog') : null;
    var region = dialog ? dialog.querySelector('[role="status"]') : null;
    setLive(region || pageLive, message);
  }

  /* ---------- Form results after a native post ----------
     Shopify returns to the page at #<form id>. Focus that form's thank-you or error so it is read
     out. Another form of the same type on the page (footer and home newsletter) shows the same
     result and may have taken focus with autofocus; the form that was actually sent wins. */
  (function focusFormResult() {
    var id = window.location.hash ? window.location.hash.slice(1) : '';
    if (!id || id === 'main' || id === 'nav-drawer') return;
    var form = null;
    try {
      form = doc.getElementById(decodeURIComponent(id));
    } catch (error) {
      form = null;
    }
    if (!form || form.tagName !== 'FORM' || form.contains(doc.activeElement)) return;
    var result = form.querySelector('[role="status"][tabindex], [role="alert"][tabindex], [aria-invalid="true"]');
    if (result && typeof result.focus === 'function') result.focus();
  })();

  /* ---------- Dialogs: native <dialog> with showModal() gives focus trap, Escape and inert page ---------- */
  var hasModal = typeof window.HTMLDialogElement === 'function' &&
    typeof window.HTMLDialogElement.prototype.showModal === 'function';
  var returnTargets = new WeakMap();
  var fallbackDialog = null;

  function isOpen(dialog) {
    return !!dialog && (dialog.hasAttribute('open') || dialog.classList.contains('is-open'));
  }

  function setExpanded(dialog, expanded) {
    if (!dialog.id) return;
    all(doc, '[aria-controls="' + escapeSelector(dialog.id) + '"][aria-haspopup]').forEach(function (element) {
      element.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function focusables(root) {
    return all(root, 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
      .filter(isRendered);
  }

  function focusInside(dialog) {
    var target = dialog.querySelector('[data-dialog-focus]') || dialog.querySelector('[autofocus]');
    if (!target && !dialog.contains(doc.activeElement)) target = focusables(dialog)[0];
    if (target) target.focus();
  }

  function openDialog(dialog, opener) {
    if (!dialog) return;
    if (isOpen(dialog)) {
      focusInside(dialog);
      return;
    }
    returnTargets.set(dialog, opener || doc.activeElement);
    if (hasModal && dialog.tagName === 'DIALOG') {
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
    } else {
      dialog.classList.add('is-open');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      fallbackDialog = dialog;
    }
    setExpanded(dialog, true);
    focusInside(dialog);
  }

  function afterClose(dialog) {
    setExpanded(dialog, false);
    var target = returnTargets.get(dialog);
    returnTargets.delete(dialog);
    if (isRendered(target) && typeof target.focus === 'function') target.focus();
  }

  function closeDialog(dialog) {
    if (!isOpen(dialog)) return;
    if (dialog.hasAttribute('open') && typeof dialog.close === 'function') {
      dialog.close();
      /* The "close" event (handled below, for Escape) arrives in a later task; tidy up now. */
      if (returnTargets.has(dialog)) afterClose(dialog);
      return;
    }
    dialog.removeAttribute('open');
    dialog.classList.remove('is-open');
    if (fallbackDialog === dialog) fallbackDialog = null;
    afterClose(dialog);
  }

  /* "close" does not bubble; the capture phase still reaches the document. Escape closes natively. */
  doc.addEventListener('close', function (event) {
    var dialog = event.target;
    if (dialog && dialog.tagName === 'DIALOG' && returnTargets.has(dialog)) afterClose(dialog);
  }, true);

  function clickedOutside(event, element) {
    var box = element.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  /* Openers are links (they work without JavaScript); with JavaScript they act as buttons. */
  function upgradeOpener(element, id) {
    if (element.tagName === 'A') element.setAttribute('role', 'button');
    element.setAttribute('aria-haspopup', 'dialog');
    element.setAttribute('aria-controls', id);
    element.setAttribute('aria-expanded', 'false');
  }

  /* ---------- Navigation ---------- */
  var navDrawer = doc.getElementById('nav-drawer');
  var headerNav = doc.querySelector('.site-header__nav');

  all(doc, '[data-dialog-open]').forEach(function (element) {
    var id = element.getAttribute('data-dialog-open');
    if (doc.getElementById(id)) upgradeOpener(element, id);
  });
  all(doc, 'a[data-dialog-close]').forEach(function (element) {
    element.setAttribute('role', 'button');
  });

  /* A page opened at the id of a drawer (#nav-drawer, the no-JavaScript menu, or a filter drawer)
     would keep that drawer shown through :target. */
  (function clearDrawerHash() {
    var id = window.location.hash ? window.location.hash.slice(1) : '';
    if (!id) return;
    var target = null;
    try {
      target = doc.getElementById(decodeURIComponent(id));
    } catch (error) {
      target = null;
    }
    if (!target || !target.classList.contains('drawer')) return;
    try {
      window.location.replace('#');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (error) { /* ignore */ }
  })();

  var wideScreen = window.matchMedia ? window.matchMedia('(min-width: 62rem)') : null;
  function onWideScreen() {
    if (wideScreen.matches && navDrawer) closeDialog(navDrawer);
  }
  if (wideScreen) {
    if (typeof wideScreen.addEventListener === 'function') wideScreen.addEventListener('change', onWideScreen);
    else if (typeof wideScreen.addListener === 'function') wideScreen.addListener(onWideScreen);
  }

  function closeMenus(except) {
    if (!headerNav) return;
    all(headerNav, 'details[open]').forEach(function (details) {
      if (details !== except) details.open = false;
    });
  }

  /* One header dropdown open at a time. A closed panel forgets that it was opened by hovering. */
  doc.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details || !headerNav || !headerNav.contains(details)) return;
    if (details.open) closeMenus(details);
    else details.removeAttribute('data-hover-open');
  }, true);

  if (headerNav) {
    /* Close a dropdown when keyboard focus moves to something outside it. A click on empty space
       (no new focus target) is handled by the document click listener instead. */
    headerNav.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (!next) return;
      all(headerNav, 'details[open]').forEach(function (details) {
        if (!details.contains(next)) details.open = false;
      });
    });

    /* Hover intent for mouse users on wide screens: a panel opens after a short pause over its link
       and closes shortly after the pointer leaves, unless it was opened by a click or the keyboard.
       A click on the link just after the hover opened the panel keeps it open (and pins it). */
    all(headerNav, 'details[data-header-menu]').forEach(function (details) {
      var summary = details.querySelector('summary');
      var timer = 0;
      var openedAt = 0;
      details.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse' || !wideScreen || !wideScreen.matches) return;
        window.clearTimeout(timer);
        if (details.open) return;
        timer = window.setTimeout(function () {
          details.setAttribute('data-hover-open', '');
          openedAt = Date.now();
          details.open = true;
        }, 90);
      });
      details.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        window.clearTimeout(timer);
        if (!details.open || !details.hasAttribute('data-hover-open')) return;
        timer = window.setTimeout(function () {
          if (details.hasAttribute('data-hover-open')) details.open = false;
        }, 240);
      });
      if (summary) {
        summary.addEventListener('click', function (event) {
          if (!details.open || !details.hasAttribute('data-hover-open')) return;
          details.removeAttribute('data-hover-open');
          if (Date.now() - openedAt < 800) event.preventDefault();
        });
      }
    });
  }

  /* Sticky header: a firmer edge once the page has scrolled. */
  var siteHeader = doc.querySelector('[data-site-header]');
  if (siteHeader) {
    var headerTicking = false;
    var markScrolled = function () {
      headerTicking = false;
      siteHeader.classList.toggle('is-scrolled', (window.scrollY || window.pageYOffset || 0) > 8);
    };
    window.addEventListener('scroll', function () {
      if (headerTicking) return;
      headerTicking = true;
      window.requestAnimationFrame(markScrolled);
    }, { passive: true });
    markScrolled();
  }

  /* ---------- Quantity steppers ---------- */
  function step(button) {
    var wrap = button.closest('[data-qty]');
    var input = wrap ? wrap.querySelector('input') : null;
    if (!input || input.disabled || input.readOnly) return;
    var down = !!(button.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_FOLLOWING);
    var size = parseFloat(input.step);
    if (!(size > 0)) size = 1;
    var min = input.min === '' ? 0 : parseFloat(input.min);
    var max = input.max === '' ? Infinity : parseFloat(input.max);
    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = Math.max(min, 1);
    var next = down ? Math.max(min, value - size) : Math.min(max, value + size);
    if (next === value) return;
    input.value = String(next);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    /* The cart drawer and the cart page announce their own result. */
    if (!wrap.closest('#cart-drawer') && !wrap.closest('[data-cart-page]')) announce('Quantity ' + next, wrap);
  }

  /* ---------- Cart ---------- */
  var drawer = doc.getElementById('cart-drawer');
  var onCartPage = !!doc.querySelector('[data-cart-page]');
  var canFetch = typeof window.fetch === 'function' && typeof window.DOMParser === 'function';

  function drawerReady() {
    return !!drawer && canFetch && !onCartPage;
  }

  if (drawerReady()) {
    all(doc, '[data-cart-open]').forEach(function (element) {
      upgradeOpener(element, 'cart-drawer');
    });
  }

  function countLabel(count) {
    return count + (count === 1 ? ' item' : ' items');
  }

  function updateCount(count) {
    all(doc, '[data-cart-count]').forEach(function (element) {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
    all(doc, '[data-cart-open]').forEach(function (element) {
      element.setAttribute('aria-label', 'Cart, ' + countLabel(count));
    });
  }

  function cartStatus(message) {
    var region = drawer ? drawer.querySelector('[data-cart-status]') : null;
    if (region) setLive(region, message);
    else announce(message);
  }

  function openCart(opener, source) {
    if (!drawerReady()) {
      window.location.href = routes.cart || '/cart';
      return;
    }
    var wasOpen = isOpen(drawer);
    openDialog(drawer, opener);
    if (!wasOpen) track('cart_drawer_open', { source: source || 'header' });
  }

  /* Swaps the drawer's children for freshly rendered ones; the dialog element, its open state and
     the live region stay. Focus goes back to the same control (by data-focus-key) when it still exists. */
  function renderDrawer(html) {
    if (!drawer || !html) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-drawer');
    if (!fresh) return false;

    var active = doc.activeElement;
    var hadFocus = !!active && active !== drawer && drawer.contains(active);
    var focusKey = hadFocus ? active.getAttribute('data-focus-key') : null;
    var status = drawer.querySelector('[data-cart-status]');

    Array.prototype.slice.call(drawer.children).forEach(function (child) {
      if (child !== status) drawer.removeChild(child);
    });
    Array.prototype.slice.call(fresh.children).forEach(function (child) {
      if (child.hasAttribute('data-cart-status')) return;
      drawer.insertBefore(doc.importNode(child, true), status);
    });

    var count = parseInt(fresh.getAttribute('data-item-count'), 10) || 0;
    drawer.setAttribute('data-item-count', String(count));
    updateCount(count);

    if (hadFocus || (isOpen(drawer) && !drawer.contains(doc.activeElement))) {
      var target = focusKey ? drawer.querySelector('[data-focus-key="' + escapeSelector(focusKey) + '"]') : null;
      target = target || drawer.querySelector('[data-dialog-focus]');
      if (target) target.focus();
    }
    return count;
  }

  function toResult(response) {
    return response.text().then(function (text) {
      var data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = {};
      }
      return { ok: response.ok, status: response.status, data: data || {} };
    });
  }

  function endpoint(route, fallback) {
    return String(route || fallback).replace(/\/$/, '') + '.js';
  }

  function refreshCart() {
    if (!drawer || !canFetch) return Promise.resolve(false);
    /* Ask the store root, not the current page: product URLs answer JSON requests with the product
       itself, so the section would be missing from the response. */
    var root = String(routes.root || '/');
    if (root.charAt(root.length - 1) !== '/') root += '/';
    var url = root + '?sections=' + encodeURIComponent(SECTION);
    return fetch(url, { credentials: 'same-origin' })
      .then(toResult)
      .then(function (result) {
        return result.ok ? renderDrawer(result.data[SECTION]) : false;
      })
      .catch(function () {
        return false;
      });
  }

  /* Cart requests run one after another, so quick clicks cannot overtake each other. */
  var queue = Promise.resolve();
  function enqueue(task) {
    queue = queue.then(task, task);
    return queue;
  }

  function showDrawerError(message) {
    var body = drawer.querySelector('[data-cart-body]') || drawer.querySelector('.drawer__body');
    if (!body) {
      cartStatus(message);
      return;
    }
    var note = body.querySelector('[data-cart-error]');
    if (!note) {
      note = doc.createElement('p');
      note.className = 'field__error';
      note.setAttribute('role', 'alert');
      note.setAttribute('data-cart-error', '');
      body.insertBefore(note, body.firstChild);
    }
    note.textContent = message;
  }

  /* One line: /cart/change.js. Several lines at once (a belt and its add-ons): /cart/update.js with
     updates keyed by line item key. Both answer with the re-rendered drawer section. */
  function changeItem(key, quantity) {
    return drawerRequest(endpoint(routes.cartChange, '/cart/change'), { id: key, quantity: quantity });
  }

  function updateItems(updates) {
    return drawerRequest(endpoint(routes.cartUpdate, '/cart/update'), { updates: updates });
  }

  /* The keys of a belt line and of every add-on line that shares its _ms_group. */
  function groupKeys(root, group, beltKey) {
    var keys = beltKey ? [beltKey] : [];
    all(root, '[data-group-member][data-key]').forEach(function (element) {
      var key = element.getAttribute('data-key');
      if (element.getAttribute('data-group-member') === group && keys.indexOf(key) === -1) keys.push(key);
    });
    return keys;
  }

  function groupUpdates(root, group, beltKey, quantity) {
    var updates = {};
    groupKeys(root, group, beltKey).forEach(function (key) {
      updates[key] = quantity;
    });
    return updates;
  }

  /* A quantity field in the drawer: a belt with add-ons changes its whole group, any other line alone. */
  function changeLine(input, quantity) {
    var key = input.getAttribute('data-key');
    var group = input.getAttribute('data-group');
    if (group) return updateItems(groupUpdates(drawer, group, key, quantity));
    return changeItem(key, quantity);
  }

  function saveNote(note) {
    return enqueue(function () {
      return fetch(endpoint(routes.cartUpdate, '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      })
        .then(toResult)
        .then(function (result) {
          if (!result.ok) showDrawerError(TEXT.noteFailed);
        })
        .catch(function () {
          showDrawerError(TEXT.noteFailed);
        });
    });
  }

  function drawerRequest(url, payload) {
    payload.sections = SECTION;
    payload.sections_url = window.location.pathname;
    return enqueue(function () {
      var body = drawer.querySelector('[data-cart-body]');
      if (body) body.setAttribute('aria-busy', 'true');
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(toResult)
        .then(function (result) {
          if (!result.ok) {
            return refreshCart().then(function () {
              showDrawerError(result.data.description || result.data.message || TEXT.changeFailed);
            });
          }
          var sections = result.data.sections || {};
          var rendered = sections[SECTION] ? renderDrawer(sections[SECTION]) : false;
          return (rendered === false ? refreshCart() : Promise.resolve()).then(function () {
            var count = typeof result.data.item_count === 'number'
              ? result.data.item_count
              : parseInt(drawer.getAttribute('data-item-count'), 10) || 0;
            cartStatus(count === 0 ? TEXT.empty : TEXT.updated + ' ' + countLabel(count) + ' in your cart.');
          });
        })
        .catch(function () {
          showDrawerError(TEXT.offline);
        })
        .then(function () {
          var current = drawer.querySelector('[data-cart-body]');
          if (current) current.removeAttribute('aria-busy');
        });
    });
  }

  function submitButtons(form) {
    var buttons = all(form, 'button[type="submit"], input[type="submit"]');
    if (form.id) buttons = buttons.concat(all(doc, '[type="submit"][form="' + escapeSelector(form.id) + '"]'));
    return buttons.filter(function (button) {
      return button.name !== 'checkout';
    });
  }

  function setBusy(form, buttons, busy) {
    if (busy) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
    buttons.forEach(function (button) {
      if (busy) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('data-ms-label', button.textContent);
        if (button.tagName === 'BUTTON') button.textContent = TEXT.adding;
      } else {
        button.removeAttribute('aria-busy');
        if (button.hasAttribute('data-ms-label')) {
          if (button.tagName === 'BUTTON') button.textContent = button.getAttribute('data-ms-label');
          button.removeAttribute('data-ms-label');
        }
      }
    });
  }

  function formError(form, message) {
    var note = form.querySelector('[data-cart-error]');
    if (!message) {
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = doc.createElement('p');
      note.className = 'field__error';
      note.setAttribute('role', 'alert');
      note.setAttribute('data-cart-error', '');
      form.appendChild(note);
    }
    note.textContent = message;
  }

  function addToCart(form, submitter) {
    var buttons = submitButtons(form);
    formError(form, '');
    setBusy(form, buttons, true);

    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);

    return fetch(endpoint(routes.cartAdd, '/cart/add'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(toResult, function () {
        /* Network failure before anything was added: let the browser post the form instead. */
        setBusy(form, buttons, false);
        HTMLFormElement.prototype.submit.call(form);
        return null;
      })
      .then(function (result) {
        if (!result) return;
        setBusy(form, buttons, false);
        if (!result.ok) {
          formError(form, result.data.description || result.data.message || TEXT.addFailed);
          return;
        }
        var sections = result.data.sections || {};
        var rendered = false;
        try {
          rendered = renderDrawer(sections[SECTION]);
        } catch (error) {
          rendered = false;
        }
        /* The item is in the cart either way; never post the form a second time. */
        (rendered === false ? refreshCart() : Promise.resolve(rendered)).then(function (state) {
          if (state === false) {
            window.location.href = routes.cart || '/cart';
            return;
          }
          /* Focus returns to the button that was used, or to the main one if that is no longer shown. */
          var opener = [submitter].concat(buttons).filter(isRendered)[0] || null;
          openCart(opener, 'add_to_cart');
          cartStatus(TEXT.added + ' ' + countLabel(parseInt(drawer.getAttribute('data-item-count'), 10) || 0) + ' in your cart.');
        });
      });
  }

  if (drawer) {
    var timers = {};

    drawer.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches || !canFetch) return;

      /* The order note is saved as soon as it changes, so a redraw of the drawer cannot lose it. */
      if (input.matches('[data-cart-note]')) {
        saveNote(input.value);
        return;
      }

      if (!input.matches('input[data-key]')) return;
      var key = input.getAttribute('data-key');
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) {
        input.value = input.defaultValue;
        return;
      }
      window.clearTimeout(timers[key]);
      timers[key] = window.setTimeout(function () {
        delete timers[key];
        if (String(quantity) !== input.defaultValue) changeLine(input, quantity);
      }, 400);
    });

    var checkoutClicked = false;

    drawer.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (!target) return;
      checkoutClicked = !!target.closest('[name="checkout"]');
      var remove = target.closest('[data-cart-remove]');
      if (!remove || !canFetch) return;
      event.preventDefault();
      var key = remove.getAttribute('data-key');
      var group = remove.getAttribute('data-group');
      /* Removing a belt removes its add-ons; an add-on's own remove button removes only that line. */
      if (group) updateItems(groupUpdates(drawer, group, key, 0));
      else changeItem(key, 0);
    });

    /* Enter in a quantity field submits through the hidden "update" button; Check out posts natively.
       event.submitter is missing in older browsers, so the last click on Check out counts too. */
    drawer.addEventListener('submit', function (event) {
      var submitter = event.submitter;
      var toCheckout = submitter ? submitter.name === 'checkout' : checkoutClicked;
      checkoutClicked = false;
      if (toCheckout || !canFetch) return;
      event.preventDefault();
      all(drawer, 'input[data-key]').forEach(function (input) {
        var quantity = parseInt(input.value, 10);
        if (!isNaN(quantity) && quantity >= 0 && String(quantity) !== input.defaultValue) {
          window.clearTimeout(timers[input.getAttribute('data-key')]);
          changeLine(input, quantity);
        }
      });
    });
  }

  /* ---------- Cart page (sections/main-cart.liquid): native form, kept in step for belt builds ---------- */
  var cartPage = doc.querySelector('[data-cart-page]');
  if (cartPage) {
    /* Each add-on posts a hidden updates[key] field; it follows the belt's quantity field, so
       "Update cart" (or Check out) changes the whole build at once. */
    var follow = function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('input[data-group]')) return;
      var group = input.getAttribute('data-group');
      all(cartPage, 'input[data-group-follow]').forEach(function (follower) {
        if (follower.getAttribute('data-group-follow') === group) follower.value = input.value;
      });
    };
    cartPage.addEventListener('input', follow);
    cartPage.addEventListener('change', follow);

    /* Removing a belt removes its add-ons in one request, then the page reloads with the new cart.
       If the request fails, the link's own address removes the belt alone. */
    cartPage.addEventListener('click', function (event) {
      var link = event.target && event.target.closest ? event.target.closest('[data-cart-group-remove]') : null;
      if (!link || !canFetch) return;
      event.preventDefault();
      if (link.getAttribute('aria-disabled') === 'true') return;
      link.setAttribute('aria-disabled', 'true');
      var fallback = link.getAttribute('href');
      var updates = groupUpdates(cartPage, link.getAttribute('data-cart-group-remove'), link.getAttribute('data-key'), 0);
      fetch(endpoint(routes.cartUpdate, '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(toResult)
        .then(function (result) {
          window.location.href = result.ok ? (routes.cart || '/cart') : fallback;
        })
        .catch(function () {
          window.location.href = fallback;
        });
    });
  }

  /* AJAX add to cart: any <form action="/cart/add" data-ajax-cart>. Other forms post as usual. */
  doc.addEventListener('submit', function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form || form.tagName !== 'FORM' || !form.hasAttribute('data-ajax-cart')) return;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1 || !drawerReady()) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    addToCart(form, event.submitter || null);
  });

  /* ---------- Shared listeners ---------- */
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var stepper = target.closest('[data-qty] .qty__btn');
    if (stepper) {
      step(stepper);
      return;
    }

    var opener = target.closest('[data-dialog-open]');
    if (opener) {
      var dialog = doc.getElementById(opener.getAttribute('data-dialog-open'));
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
      }
      return;
    }

    var cartOpener = target.closest('[data-cart-open]');
    if (cartOpener) {
      if (drawerReady()) {
        event.preventDefault();
        openCart(cartOpener, cartOpener.getAttribute('data-cart-source') || 'header');
      }
      return;
    }

    var closer = target.closest('[data-dialog-close]');
    if (closer) {
      var owner = closer.closest('dialog, .drawer, .dialog');
      if (owner) {
        event.preventDefault();
        closeDialog(owner);
      }
      return;
    }

    /* A click on the backdrop of a drawer lands on the dialog itself, outside its box. */
    if (target.classList.contains('drawer') && isOpen(target) && clickedOutside(event, target)) {
      closeDialog(target);
      return;
    }

    /* Following a link inside the menu drawer closes it (it may point into the current page). */
    if (navDrawer && isOpen(navDrawer) && target.closest('#nav-drawer a[href]')) {
      closeDialog(navDrawer);
    }

    if (headerNav && !headerNav.contains(target)) closeMenus();
  });

  doc.addEventListener('keydown', function (event) {
    var target = event.target;

    /* Links upgraded to buttons also answer the space bar. */
    if ((event.key === ' ' || event.key === 'Spacebar') && target && target.tagName === 'A' && target.getAttribute('role') === 'button') {
      event.preventDefault();
      target.click();
      return;
    }

    if (event.key === 'Escape' || event.key === 'Esc') {
      if (fallbackDialog) {
        closeDialog(fallbackDialog);
        return;
      }
      var openMenu = headerNav && target && target.closest ? target.closest('.site-header__nav details[open]') : null;
      if (openMenu) {
        openMenu.open = false;
        var summary = openMenu.querySelector('summary');
        if (summary) summary.focus();
      } else {
        closeMenus();
      }
      return;
    }

    /* Focus trap for browsers without native modal dialogs. */
    if (event.key === 'Tab' && fallbackDialog) {
      var items = focusables(fallbackDialog);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (doc.activeElement === first || !fallbackDialog.contains(doc.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (doc.activeElement === last || !fallbackDialog.contains(doc.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* Back/forward cache: a restored page may show an old cart. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    all(doc, 'form[aria-busy="true"]').forEach(function (form) {
      setBusy(form, submitButtons(form), false);
    });
    if (drawer && canFetch) refreshCart();
  });

  window.MS = {
    openCart: openCart,
    refreshCart: refreshCart,
    openDialog: openDialog,
    closeDialog: closeDialog,
    announce: announce
  };
})();
